/* eslint-disable @typescript-eslint/no-explicit-any */
import useFlags from 'launchdarkly-react-client-sdk/lib/useFlags';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';
import { BiogenFunction, VeevaVaultBiogenFunction, Channel, VaultDocumentStatuses } from 'basics/enums/maya.enums';
import useGetAllVeevaVaultIVADocument, { useGetAllVeevaVaultIVADocumentLazy } from 'basics/graphql/queries/getAllVeevaVaultIVADocument';
import useIVADocumentIdOptions from 'basics/options/ivaDocumentId.options';
import { CepStateType, TacticStateType } from 'basics/types/maya.types';
import { warningToast } from 'basics/utils/toast';
import { ProductsContext } from 'contexts/products';
import {
  CustomerInitiative,
  VeevaVaultIvaDocumentFilter,
  VeevaVaultDocumentLimit,
  VeevaVaultDocument,
} from 'generated/maya.types';
import cepState from 'states/cep.states';
import tacticState from 'states/tactic.states';

type FetchTacticIVADocumentsOptionsParams = {
  country?: string;
  product?: string;
  executingTeam?: string;
  includeNotCertifiedDocuments?: boolean;
};

type DocumentOption = {
  id: string;
  label: string;
};

const IVA_DOCUMENT_CHANNELS: Channel[] = [
  Channel.f2fEvent,
  Channel.f2fMeeting,
  Channel.oneToOne,
  Channel.oneToOneIVA,
  Channel.hybridEvent,
  Channel.virtualEvent,
  Channel.virtualMeeting,
];

const isApprovedDocument = (document: VeevaVaultDocument | undefined) => {
  if (!document) return false;

  return document.status === VaultDocumentStatuses.Approved
      || document.status === VaultDocumentStatuses.ApprovedForProduction
      || document.status === VaultDocumentStatuses.ApprovedWithChanges;
};

const isDocumentAvailableForExecutingTeam = (
  document: VeevaVaultDocument,
  executingTeam?: string,
) => {
  switch (executingTeam) {
    case BiogenFunction.Commercial:
      return Boolean(document.executingTeam?.includes(VeevaVaultBiogenFunction.Commercial));
    case BiogenFunction.Medical:
      return Boolean(document.executingTeam?.includes(VeevaVaultBiogenFunction.Medical));
    case BiogenFunction.MarketAccess:
      return Boolean(document.executingTeam?.includes(VeevaVaultBiogenFunction.MarketAccess));
    default:
      return false;
  }
};

const mapDocumentsToOptions = (documents: VeevaVaultDocument[]): DocumentOption[] => {
  return documents.map((document) => ({
    id: document.id.toString(),
    label: `[${document.documentNumber}] [${document.status}] ${document.name}`,
  }));
};

export const useFetchTacticIVADocumentsOptions = () => {
  const flags = useFlags();
  const { getAllVeevaVaultIVADocumentLazy } = useGetAllVeevaVaultIVADocumentLazy();

  const fetchTacticIVADocumentsOptions = async ({
    country,
    product,
    executingTeam,
    includeNotCertifiedDocuments = false,
  }: FetchTacticIVADocumentsOptionsParams): Promise<DocumentOption[]> => {
    if (!country || !product || !flags.mayaAkma382EnableIvaTacticField) {
      return [];
    }

    const { data } = await getAllVeevaVaultIVADocumentLazy({
      variables: {
        limit: {
          limit: 5000,
        },
        filters: {
          country,
          product,
        },
      },
    });

    const filteredDocuments = (data?.getAllVeevaVaultIVADocument || [])
      .filter((document: VeevaVaultDocument) => isDocumentAvailableForExecutingTeam(document, executingTeam))
      .filter((document: VeevaVaultDocument) => {
        if (includeNotCertifiedDocuments || flags.mayaAkma759ShowNotCertifiedDocuments) {
          return true;
        }

        return isApprovedDocument(document);
      });

    return mapDocumentsToOptions(filteredDocuments);
  };

  return {
    fetchTacticIVADocumentsOptions,
  };
};

const useIVADocuments = (
  executingTeam: BiogenFunction,
  channel: Channel,
  selectedIVADocumentId: string,
  setValue: (fieldName: string, fieldValue: any) => void,
) => {
  const flags = useFlags();
  const { t } = useTranslation();
  const cepStateValue = useSnapshot(cepState) as CepStateType;
  const { productList } = useContext(ProductsContext);
  const currentProduct = productList?.find((product) => product.productExternalID === cepStateValue?.cep?.product);
  const customerInitiative = cepStateValue.cep?.customerInitiative as CustomerInitiative;
  const { country } = customerInitiative;
  const [IVADocuments, setIVADocument] = useState<VeevaVaultDocument[]>([]);
  const [loadingStarted, setLoadingStarted] = useState(false);
  const [showNotCertifiedIVADocuments, setShowNotCertifiedIVADocuments] = useState(false);
  const tacticStateValue = useSnapshot(tacticState) as TacticStateType;
  const currentDocumentId = tacticStateValue?.tactic?.IVADocumentId;

  const vaultDocumentFilter: VeevaVaultIvaDocumentFilter = {
    country,
    product: currentProduct?.productID,
  };

  const vaultDocumentLimit: VeevaVaultDocumentLimit = {
    limit: 5000,
  };

  const {
    data: documentsData,
    loading: allVeevaVaultIVADocumentsLoading,
  } = useGetAllVeevaVaultIVADocument(vaultDocumentLimit, vaultDocumentFilter);

  const allIVADocumentsOptions = useIVADocumentIdOptions(IVADocuments);

  useEffect(() => {
    const filteredDocumentsByExecutingTeam = documentsData.filter((document) => {
      return isDocumentAvailableForExecutingTeam(document, executingTeam);
    });

    let filteredDocuments;

    if (flags.mayaAkma759ShowNotCertifiedDocuments) {
      filteredDocuments = filteredDocumentsByExecutingTeam.filter((document) => {
        if (!showNotCertifiedIVADocuments && isApprovedDocument(document)) return document;
        if (showNotCertifiedIVADocuments) return document;

        return false;
      });
    } else {
      filteredDocuments = filteredDocumentsByExecutingTeam.filter((document) => {
        return isApprovedDocument(document) || document.id.toString() === currentDocumentId;
      });
    }

    setIVADocument(filteredDocuments);

    if (allVeevaVaultIVADocumentsLoading) {
      setLoadingStarted(true);
    }

    const isLoadedDocuments = loadingStarted && !allVeevaVaultIVADocumentsLoading && flags.mayaAkma382EnableIvaTacticField;
    const isValidChannel = IVA_DOCUMENT_CHANNELS.includes(channel);

    if (isLoadedDocuments && !filteredDocuments.length && executingTeam && isValidChannel) {
      warningToast(t('maya_no_iva_documents_warning'));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executingTeam, showNotCertifiedIVADocuments, allVeevaVaultIVADocumentsLoading]);

  useEffect(() => {
    const currentDocument = documentsData?.find((document) => document.id.toString() === currentDocumentId);

    if (currentDocument && !isApprovedDocument(currentDocument)) {
      setShowNotCertifiedIVADocuments(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allVeevaVaultIVADocumentsLoading]);

  const handleIVADocumentsFilterChanging = (value: boolean) => {
    setShowNotCertifiedIVADocuments(value);
  };

  useEffect(() => {
    const notEqualDocuments = selectedIVADocumentId && selectedIVADocumentId !== currentDocumentId;
    const currentDocument = documentsData?.find((document) => document?.id.toString() === selectedIVADocumentId);

    if (notEqualDocuments && !isApprovedDocument(currentDocument)) {
      warningToast(t('maya_not_approved_documents_warning'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIVADocumentId]);

  useEffect(() => {
    const isLoadedDocuments = loadingStarted && !allVeevaVaultIVADocumentsLoading && flags.mayaAkma382EnableIvaTacticField;

    if (!isLoadedDocuments || !selectedIVADocumentId || !executingTeam) {
      return;
    }

    const selectedDocument = documentsData?.find((document) => {
      return document?.id.toString() === selectedIVADocumentId;
    });

    const isSelectedDocumentAvailable = selectedDocument
        && isDocumentAvailableForExecutingTeam(selectedDocument, executingTeam)
        && (
          flags.mayaAkma759ShowNotCertifiedDocuments
            ? showNotCertifiedIVADocuments || isApprovedDocument(selectedDocument)
            : isApprovedDocument(selectedDocument)
        );

    if (!isSelectedDocumentAvailable) {
      setValue('IVADocumentId', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    executingTeam,
    selectedIVADocumentId,
    documentsData,
    loadingStarted,
    allVeevaVaultIVADocumentsLoading,
    flags.mayaAkma382EnableIvaTacticField,
    flags.mayaAkma759ShowNotCertifiedDocuments,
    showNotCertifiedIVADocuments,
  ]);

  return {
    allIVADocumentsOptions,
    allVeevaVaultIVADocumentsLoading,
    showNotCertifiedIVADocuments,
    handleIVADocumentsFilterChanging,
  };
};

export default useIVADocuments;
